import { Check, Prohibit, Quotes, Target } from "@phosphor-icons/react";
import { TYPE_ACCENT } from "@/features/decodificar/content";
import { getPlaybook } from "@/features/decodificar/express/playbooks";
import type { ClassifyResult } from "@/features/decodificar/express/types";
import { EnneagramBadge } from "@/components/shared/EnneagramBadge";

interface PlaybookCardProps {
  result: ClassifyResult;
}

export function PlaybookCard({ result }: PlaybookCardProps) {
  const primary = getPlaybook(result.primary);
  const secondary = result.secondary ? getPlaybook(result.secondary) : null;
  const accent = TYPE_ACCENT[result.primary];
  const confidencePct = Math.round(result.confidence * 100);

  return (
    <article
      className="rounded-2xl border bg-card p-5 shadow-sm"
      style={{ borderTopColor: accent, borderTopWidth: 4 }}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <EnneagramBadge
            type={primary.id}
            label={primary.name}
            size="featured"
            accentColor={accent}
          />
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
              Código provável
            </p>
            <h3 className="text-lg font-semibold text-foreground">
              {primary.code} {primary.name}
            </h3>
            <p className="text-xs text-muted-foreground">{primary.archetype}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold" style={{ color: accent }}>
            {confidencePct}%
          </p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
            confiança
          </p>
        </div>
      </header>

      {result.ambiguous && (
        <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          Sinais misturados. Use como hipótese, não certeza.
          {secondary && (
            <>
              {" "}Considere também <strong>{secondary.code} {secondary.name}</strong>.
            </>
          )}
        </div>
      )}

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <section>
          <h4 className="flex items-center gap-2 text-sm font-semibold text-emerald-700 dark:text-emerald-400">
            <Check weight="bold" /> Faça agora
          </h4>
          <ul className="mt-2 space-y-1.5 text-sm text-foreground">
            {primary.doThis.map((item) => (
              <li key={item} className="leading-snug">
                · {item}
              </li>
            ))}
          </ul>
        </section>

        <section>
          <h4 className="flex items-center gap-2 text-sm font-semibold text-destructive dark:text-red-400">
            <Prohibit weight="bold" /> Não faça
          </h4>
          <ul className="mt-2 space-y-1.5 text-sm text-foreground">
            {primary.avoid.map((item) => (
              <li key={item} className="leading-snug">
                · {item}
              </li>
            ))}
          </ul>
        </section>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <div className="rounded-lg border bg-background p-3">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <Target weight="bold" /> Frame de fechamento
          </p>
          <p className="mt-1 text-sm text-foreground">{primary.closingFrame}</p>
        </div>
        <div className="rounded-lg border bg-background p-3">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <Quotes weight="bold" /> Frase-chave dele
          </p>
          <p className="mt-1 text-sm italic text-foreground">"{primary.keyPhrase}"</p>
        </div>
      </div>

      <p className="mt-3 text-xs text-muted-foreground">
        Crença central: {primary.belief}
      </p>
    </article>
  );
}
